// PurchaseReports.js — Professional reporting hub for the Purchase module.
// One backend call returns four reports (Date / Supplier / Item / Stock
// Movement) so tab-switching is instant. Each tab has its own Excel export.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import config from '../../config';

const TAB_INFO = [
  { key: 'datewise', label: 'Date-Wise' },
  { key: 'supplierwise', label: 'Supplier-Wise' },
  { key: 'itemwise', label: 'Item-Wise' },
  { key: 'stockmovement', label: 'Stock Movement' },
];

export default function PurchaseReports() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const Companycurrency = company?.currency ?? '';

  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    supplierId: '',
  });

  const fmt = (v) =>
    `${Companycurrency}${Number(v || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fetchReports = async (overrideFilters) => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const f = overrideFilters || filters;
      const params = new URLSearchParams();
      if (f.fromDate) params.append('fromDate', f.fromDate);
      if (f.toDate) params.append('toDate', f.toDate);
      if (f.supplierId) params.append('supplierId', f.supplierId);
      const q = params.toString();
      const url = `${config.API_BASE_URL}/Home/GetProfessionalPurchaseReport/${company.id}${q ? `?${q}` : ''}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || null);
    } catch (err) {
      console.error('Reports load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    if (!company?.id) return;
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/Home/GetSuppliers/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuppliers(res.data || []);
    } catch {
      // non-blocking — filter just won't have supplier options
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    const cleared = { fromDate: '', toDate: '', supplierId: '' };
    setFilters(cleared);
    fetchReports(cleared);
  };

  // ─── Excel export per tab ─────────────────────────────────────────────
  const exportExcel = () => {
    if (!data) return;
    let rows = [];
    let sheetName = 'Report';
    let fileName = 'PurchaseReport';

    if (tab === 0) {
      sheetName = 'Date-Wise';
      fileName = 'Purchase_DateWise';
      rows = (data.dateWise || []).map((r, i) => ({
        '#': i + 1,
        Date: new Date(r.date).toLocaleDateString('en-IN'),
        'Purchase Count': r.purchaseCount,
        'Item Count': r.itemCount,
        'Total Amount': Number(r.totalAmount || 0).toFixed(2),
      }));
    } else if (tab === 1) {
      sheetName = 'Supplier-Wise';
      fileName = 'Purchase_SupplierWise';
      rows = (data.supplierWise || []).map((r, i) => ({
        '#': i + 1,
        Supplier: r.supplierName,
        Purchases: r.purchaseCount,
        'Total Amount': Number(r.totalAmount || 0).toFixed(2),
        'Last Purchase': r.lastPurchaseDate
          ? new Date(r.lastPurchaseDate).toLocaleDateString('en-IN')
          : '—',
      }));
    } else if (tab === 2) {
      sheetName = 'Item-Wise';
      fileName = 'Purchase_ItemWise';
      rows = (data.itemWise || []).map((r, i) => ({
        '#': i + 1,
        Item: r.itemName,
        'Total Qty': r.totalQty,
        'Avg Rate': Number(r.avgRate || 0).toFixed(2),
        'Total Amount': Number(r.totalAmount || 0).toFixed(2),
        Purchases: r.purchaseCount,
      }));
    } else {
      sheetName = 'Stock Movement';
      fileName = 'Purchase_StockMovement';
      rows = (data.stockMovement || []).map((r, i) => ({
        '#': i + 1,
        Date: new Date(r.createdDate).toLocaleString('en-IN'),
        Item: r.storeItemName,
        Type: r.transactionType,
        Quantity: r.quantity,
        'Balance After': r.balanceAfter,
        Remarks: r.remarks,
      }));
    }

    if (rows.length === 0) {
      toast.info('No data to export for this tab.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const printReport = () => {
    window.print();
  };

  const activeRows = useMemo(() => {
    if (!data) return [];
    if (tab === 0) return data.dateWise || [];
    if (tab === 1) return data.supplierWise || [];
    if (tab === 2) return data.itemWise || [];
    return data.stockMovement || [];
  }, [data, tab]);

  if (loading)
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress />
      </Box>
    );

  return (
    <Container maxWidth='xl' sx={{ mt: 3, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AssessmentIcon sx={{ fontSize: 32, color: '#1565c0' }} />
        <Typography variant='h4' fontWeight='bold'>
          Purchase Reports
        </Typography>
      </Box>

      {/* Summary tiles */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={4}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Total Purchase Count
              </Typography>
              <Typography variant='h4' fontWeight='bold' color='#1565c0'>
                {data?.purchaseCount ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={4}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Grand Total
              </Typography>
              <Typography variant='h5' fontWeight='bold' color='#2e7d32'>
                {fmt(data?.grandTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant='caption' color='text.secondary'>
                Unique Suppliers in Period
              </Typography>
              <Typography variant='h5' fontWeight='bold' color='#ef6c00'>
                {data?.supplierWise?.length ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
          <TextField
            type='date'
            size='small'
            label='From'
            InputLabelProps={{ shrink: true }}
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
          />
          <TextField
            type='date'
            size='small'
            label='To'
            InputLabelProps={{ shrink: true }}
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
          />
          <TextField
            select
            size='small'
            label='Supplier'
            value={filters.supplierId}
            onChange={(e) =>
              setFilters({ ...filters, supplierId: e.target.value })
            }
            sx={{ minWidth: 220 }}>
            <MenuItem value=''>All suppliers</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.supplierName}
              </MenuItem>
            ))}
          </TextField>
          <Button variant='contained' onClick={() => fetchReports()}>
            Apply
          </Button>
          <Button variant='outlined' onClick={clearFilters}>
            Clear
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant='contained'
            color='success'
            startIcon={<FileDownloadIcon />}
            onClick={exportExcel}>
            Export Excel
          </Button>
          <Button variant='outlined' onClick={printReport}>
            Print
          </Button>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{ borderBottom: '1px solid #e0e0e0' }}>
          {TAB_INFO.map((t, i) => (
            <Tab
              key={t.key}
              label={t.label}
              sx={{ fontWeight: 600, textTransform: 'none' }}
            />
          ))}
        </Tabs>

        <TableContainer>
          <Table>
            {tab === 0 && (
              <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Purchases
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Items
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Total Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                        No data in this date range
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeRows.map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {new Date(r.date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </TableCell>
                        <TableCell align='right'>{r.purchaseCount}</TableCell>
                        <TableCell align='right'>{r.itemCount}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          {fmt(r.totalAmount)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </>
            )}

            {tab === 1 && (
              <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Purchases
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Total Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Purchase</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                        No supplier purchases found
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeRows.map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.supplierName}
                          {!r.supplierId && (
                            <Chip
                              size='small'
                              label='Unlinked'
                              sx={{ ml: 1 }}
                              variant='outlined'
                            />
                          )}
                        </TableCell>
                        <TableCell align='right'>{r.purchaseCount}</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          {fmt(r.totalAmount)}
                        </TableCell>
                        <TableCell>
                          {r.lastPurchaseDate
                            ? new Date(r.lastPurchaseDate).toLocaleDateString('en-IN')
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </>
            )}

            {tab === 2 && (
              <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Total Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Avg Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Total Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Purchases
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                        No item-level data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeRows.map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.itemName}</TableCell>
                        <TableCell align='right'>{r.totalQty}</TableCell>
                        <TableCell align='right'>
                          {fmt(r.avgRate)}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          {fmt(r.totalAmount)}
                        </TableCell>
                        <TableCell align='right'>{r.purchaseCount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </>
            )}

            {tab === 3 && (
              <>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Balance After
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align='center' sx={{ py: 4 }}>
                        No stock movement in range
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeRows.map((r, i) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          {new Date(r.createdDate).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>{r.storeItemName}</TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={r.transactionType}
                            color={
                              r.quantity < 0
                                ? 'error'
                                : r.transactionType === 'OPENING_STOCK'
                                  ? 'info'
                                  : 'success'
                            }
                          />
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            color: r.quantity < 0 ? '#c62828' : '#2e7d32',
                            fontWeight: 600,
                          }}>
                          {r.quantity > 0 ? '+' : ''}
                          {r.quantity}
                        </TableCell>
                        <TableCell align='right'>{r.balanceAfter}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                          {r.remarks}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
}
